import React, { useContext, useEffect, useRef, useState, useCallback } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import MGrid, { FILTER_TYPE_NONE, MGridColumnDefinition } from "components/Shared/MGrid.tsx";
import { MSpinner } from "components/Shared";
import { CaseAttributesResponseDto } from "interfaces/Mueller.Business.Models";
import { userHasRight } from "selectors/rights";
import { CASE_ATTRIBUTE_EDIT, CASE_ATTRIBUTE_LIMITED_VIEW, CASE_ATTRIBUTE_VIEW } from "typeConstants/rights";
import {
  addCaseAttribute,
  editCaseAttribute,
  deleteCaseAttribute,
  getCaseAttributes,
  getLimitedCaseAttributes,
} from "requests/caseAttributes";
import responseHandler from "responseHandler";
import { DialogContext, DialogProviderValue } from "components/Shared/DialogProvider";
import { CardText, TextField } from "react-md";

interface CaseAttributesCardProps {
  controlNumber: string;
  gridHeightPixels?: number;
  // state
  canViewCaseAttributes?: boolean;
  canViewLimitedCaseAttributes?: boolean;
  canEditCaseAttributes?: boolean;
  // dispatch
  makeErrorToast?: (text: string) => void;
  makeSuccessToast?: (text: string) => void;
}

const CaseAttributesCard = (props: CaseAttributesCardProps) => {
  const [caseAttributes, setCaseAttributes] = useState<CaseAttributesResponseDto[]>([]);
  const [isFetching, setIsFetching] = useState<boolean>(true);
  const dialogContext = useContext<DialogProviderValue>(DialogContext);

  const mounted = useRef(true);
  const newAttributeValue = useRef<string>(null);
  const newAttributeName = useRef<string>(null);

  const fetchingErrorText: string = "Error fetching case attributes.";

  const fetchAttributes = useCallback(() => {
    setIsFetching(true);
    getCaseAttributes(
      props.controlNumber,
      mounted,
      (response) => {
        setCaseAttributes(response.sort((a, b) => a.name.localeCompare(b.name)));
        setIsFetching(false);
      },
      () => {
        props.makeErrorToast?.(fetchingErrorText);
        setIsFetching(false);
      },
    );
  }, [props.controlNumber, props.makeErrorToast, mounted]);

  const fetchLimitedCaseAttributes = useCallback(() => {
    setIsFetching(true);
    getLimitedCaseAttributes(
      props.controlNumber,
      mounted,
      (response) => {
        setCaseAttributes(response.sort((a, b) => a.name.localeCompare(b.name)));
        setIsFetching(false);
      },
      () => {
        props.makeErrorToast?.(fetchingErrorText);
        setIsFetching(false);
      },
    );
  }, [props.controlNumber, props.makeErrorToast, mounted]);

  let fetchFunction: () => void = null;

  if (props.canViewCaseAttributes) {
    fetchFunction = fetchAttributes;
  } else if (props.canViewLimitedCaseAttributes) {
    fetchFunction = fetchLimitedCaseAttributes;
  }
  else {
    // no rights to view, don't show the card
    return null;
  }

  useEffect(() => {
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (fetchFunction) {
      setIsFetching(true);
      fetchFunction();
    }
  }, [props.controlNumber]);

  // Case Attributes are fetched in CaseDetails component
  if (isFetching) {
    return <MSpinner />;
  }

  const validateAttributeValue = (): boolean => {
    if (!newAttributeValue.current || newAttributeValue.current.trim() === "") {
      props.makeErrorToast?.("Attribute value is required.");
      return false;
    }
    return true;
  };

  const validateNameValue = (): boolean => {
    if (!newAttributeName.current || newAttributeName.current.trim() === "") {
      props.makeErrorToast?.("Attribute name is required.");
      return false;
    }
    return true;
  };

  const onAddConfirm = () => {
    if (!validateAttributeValue() || !validateNameValue()) {
      return false;
    }
    addCaseAttribute(
      {
        controlIdNumber: props.controlNumber as unknown as number,
        name: newAttributeName.current,
        value: newAttributeValue.current,
      },
      mounted,
      () => {
        fetchFunction?.();
        dialogContext.hide();
        props.makeSuccessToast?.("Case attribute added successfully!");
      },
      () => {
        props.makeErrorToast?.("Error adding case attribute, please try again");
        dialogContext.hide();
      },
    );
  };

  const onEditConfirm = (attributeIndex: number) => {
    if (!validateAttributeValue()) {
      return false;
    }
    editCaseAttribute(
      caseAttributes[attributeIndex].id,
      //@ts-ignore
      {
        value: newAttributeValue.current,
      },
      mounted,
      () => {
        fetchFunction?.();
        dialogContext.hide();
        props.makeSuccessToast?.("Case attribute updated successfully!");
      },
      () => {
        props.makeErrorToast?.("Error updating case attribute, please try again");
      },
    );
  };

  const onDeleteConfirm = (attributeIndex: number) => {
    deleteCaseAttribute(
      caseAttributes[attributeIndex].id,
      mounted,
      () => {
        fetchFunction?.();
        dialogContext.hide();
        props.makeSuccessToast?.("Case attribute deleted successfully!");
      },
      () => {
        props.makeErrorToast?.("Error deleting case attribute, please try again");
        dialogContext.hide();
      },
    );
  };

  const getAddDialogContent = () => (
    <CardText>
      Name: <TextField onChange={(v, e) => { newAttributeName.current = v as string; }} />
        <br /><br />
      Value: <TextField onChange={(v, e) => { newAttributeValue.current = v as string; }} />
    </CardText>
  );

  const getEditDialogContent = (attributeIndex: number) => (
    <CardText>
      Name: <b>{caseAttributes[attributeIndex].name}</b>
      <br /><br />
      Attribute Value:
      <TextField 
        onChange={(v, e) => { newAttributeValue.current = v as string; }}
      />
    </CardText>
  );

  const getDeleteDialogContent = (attributeName: string) => (
    <div>
      Are you sure you want to delete the attribute "
      {attributeName}"?
    </div>
  );

  const onEditButtonHit = (rowIndex: number) => {
    dialogContext.show("Edit Case Attribute", getEditDialogContent(rowIndex), [
      {
        label: "Save",
        isPrimary: true,
        onClick: () => {
          // return the value to make sure the dialog stays open if validation fails
          return onEditConfirm(rowIndex);
        },
      },
      {
        label: "Cancel",
        onClick: () => {
          dialogContext.hide();
          newAttributeValue.current = null;
        },
      },
    ]);
  };

  const onDeleteButtonHit = (rowIndex: number) => {
    dialogContext.show("Delete Case Attribute", getDeleteDialogContent(caseAttributes[rowIndex].name), [
      {
        label: "Delete",
        isPrimary: false,
        onClick: () => {
          onDeleteConfirm(rowIndex);
        },
      },
      {
        label: "Cancel",
        isPrimary: true,
        onClick: () => {
          dialogContext.hide();
        },
      },
    ]);
  };

  const columnDefinitions: MGridColumnDefinition[] = [
    {
      key: "name",
      name: "Name",
      dataSource: "name",
      filterType: FILTER_TYPE_NONE,
      sortable: false,
      resizable: true,
    },
    {
      key: "value",
      name: "Value",
      dataSource: "value",
      filterType: FILTER_TYPE_NONE,
      sortable: false,
      resizable: true,
    },
  ];

  if (props.canEditCaseAttributes) {
    columnDefinitions.push({
      key: "edit",
      name: "Edit",
      // eslint-disable-next-line react/display-name
      dataSource: (row) => (
        <i
          className="material-icons material-icons"
          onClick={() => {
            onEditButtonHit(caseAttributes.findIndex((attr) => attr.name === row.name));
          }}
          style={{ cursor: "pointer" }}
        >
          edit
        </i>
      ),
      sortable: false,
      resizable: false,
      filterType: FILTER_TYPE_NONE,
      width: 45,
      tooltip: "Edit Attribute",
    });
    columnDefinitions.push({
      key: "delete",
      name: "Delete",
      // eslint-disable-next-line react/display-name
      dataSource: (row) => (
        <i
          className="material-icons material-icons"
          onClick={() => {
            onDeleteButtonHit(caseAttributes.findIndex((attr) => attr.name === row.name));
          }}
          style={{ cursor: "pointer" }}
        >
          delete
        </i>
      ),
      sortable: false,
      resizable: false,
      filterType: FILTER_TYPE_NONE,
      width: 60,
      tooltip: "Delete Attribute",
    });
  }

  return (
        <MGrid
          headerText="Case Attributes"
          actions={props.canEditCaseAttributes ?[
            {
                icon: "add",
                helpText: "Add Case Attribute",
                onClick: () => {
                  newAttributeValue.current = null;
                  dialogContext.show("Add Case Attribute", getAddDialogContent(), [
                    {
                      label: "Add",
                      isPrimary: true,
                      // return the value to make sure the dialog stays open if validation fails
                      onClick: () => { return onAddConfirm(); },
                    },
                    {
                      label: "Cancel",
                      onClick: () => {
                        dialogContext.hide();
                        newAttributeValue.current = null;
                        newAttributeName.current = null;
                      }
                    }
                  ])
                }
            }
          ] : []}
          className="case-attributes-grid"
          columnDefinitions={columnDefinitions}
          data={caseAttributes}
          displayNumOfItemsFound={false}
          cardStyle
          height={props.gridHeightPixels || undefined}
        />
  );
};

const mapStateToProps = (state) => {
  return {
    canViewCaseAttributes: userHasRight(CASE_ATTRIBUTE_VIEW)(state),
    canViewLimitedCaseAttributes: userHasRight(CASE_ATTRIBUTE_LIMITED_VIEW)(state),
    canEditCaseAttributes: userHasRight(CASE_ATTRIBUTE_EDIT)(state),
  };
};

const mapDispatchToProps = (dispatch) => ({
  makeErrorToast(text: string) {
    responseHandler.customToast(text, "error", dispatch);
  },
  makeSuccessToast(text: string) {
    responseHandler.customToast(text, "success", dispatch);
  },
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(CaseAttributesCard));
